import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectProps {
  label?: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function MultiSelect({
  label,
  options,
  value = [],
  onChange,
  placeholder = 'Select options...',
  className,
  disabled = false,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const removeValue = (val: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== val));
  };

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={cn('w-full', className)} ref={containerRef}>
      {label && (
        <span className="mb-1.5 block text-body-sm font-medium text-on-surface">
          {label}
        </span>
      )}
      <div className="relative">
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={cn(
            'flex min-h-[40px] w-full items-center justify-between rounded-lg border bg-card px-3 py-1.5 text-left text-data-md text-on-surface shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            disabled ? 'cursor-not-allowed bg-surface-variant opacity-60' : 'cursor-pointer border-input',
            isOpen && 'ring-2 ring-primary/40'
          )}
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-1.5 pr-6">
            {value.length === 0 ? (
              <span className="text-on-surface-subtle">{placeholder}</span>
            ) : (
              options
                .filter((o) => value.includes(o.value))
                .map((o) => (
                  <span
                    key={o.value}
                    className="inline-flex items-center gap-1 rounded bg-primary-container px-2 py-0.5 text-data-sm font-medium text-on-primary-container"
                  >
                    {o.label}
                    {!disabled && (
                      <button
                        type="button"
                        onClick={(e) => removeValue(o.value, e)}
                        className="rounded-full p-0.5 hover:bg-on-primary-container/10"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))
            )}
          </div>
          <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-card border border-border bg-popover p-1 shadow-surface-2">
            {/* Search Input */}
            <div className="relative mb-1 p-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-subtle" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="h-8 w-full rounded border border-outline bg-surface pl-8 pr-3 text-data-sm text-on-surface focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {/* Options List */}
            <div className="space-y-0.5">
              {filteredOptions.length === 0 ? (
                <div className="p-2 text-center text-body-sm text-on-surface-subtle">
                  No results found
                </div>
              ) : (
                filteredOptions.map((o) => {
                  const isSelected = value.includes(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => toggleOption(o.value)}
                      className={cn(
                        'flex w-full items-center justify-between rounded px-3 py-1.5 text-left text-data-md transition-colors',
                        isSelected ? 'bg-primary/10 text-primary font-medium' : 'text-on-surface hover:bg-surface-variant/45'
                      )}
                    >
                      <span>{o.label}</span>
                      {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
